import React, { useEffect, useState } from "react";

// region Third party
import { UseFormReturn } from "react-hook-form";
import * as _ from "lodash";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { getValidationErrors } from "@npm-telluria-tecnologia/telluria-react-hooks";
// endregion Third party
// region Atoms
import Step from "@atoms/Step";
// endregion Atoms
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Libraries
import Yup from "@libraries/yup";
import { Fab } from "@libraries/mui/components";
import { SetStateAction } from "@libraries/jotai";
import { ArrowBack, ArrowForward, Check } from "@libraries/mui/icons";
// endregion Libraries
// region Styles
import ActionDialog from "@atoms/ActionDialog";
import * as Styled from "./styles";
// endregion Styles

// region Types
type TMode = "CREATE" | "EDIT" | "COPY";
// endregion Types

// region Interfaces
export interface IStepTabProps {
  id: string;
  tabLabel: string;
  schema?: Yup.AnyObjectSchema;
  invisible?: boolean;
  disabled?: boolean;
  advanceTabWarning?: {
    title?: string;
    message?: string;
    mode: TMode[];
  };
  returnTabWarning?: {
    title?: string;
    message?: string;
    mode: TMode[];
  };
}

export interface IOnChangeTabResponse {
  stepTabIsAvailable: boolean;
  indexNextAvailableStepTab: number;
  toastMessage: string;
  additionalToastMessages?: string[];
  toastMessageType?: "success" | "error" | "info";
}

interface IStepTabsProps {
  content: IStepTabProps[];
  selectedStepTab: number;
  mode: TMode;
  disableControlButtons?: boolean;
  setSelectedStepTab: (update: SetStateAction<number>) => void;
  onChangeTab?: (indexNewTab: number) => Promise<IOnChangeTabResponse>;
  onFinish: () => void;
  showConfirmButtonOnLastStepTab?: boolean;
  additionalControlButtons?: {
    id: string;
    color?: "inherit" | "primary" | "secondary" | "default" | "success" | "error" | "info" | "warning";
    size?: "small" | "medium" | "large";
    displayFromStepTab: number;
    extended?: boolean;
    onClick: () => void;
    content: React.ReactNode;
  }[];
}

// endregion Interfaces
// region Functions
/**
 * Validates if the clicked stepTab is available
 * @param steps process guides
 * @param methods individual functions to manage the form state.
 * @param stepTabIndexClicked stepTab index clicked
 * @returns Promise object indicates whether the stepTab is available,
 * the index of the next available stepTab for redirection if needed and the error message to be displayed
 */
export async function validateStepTabForm<T = any>(
  steps: IStepTabProps[],
  methods: UseFormReturn<T, any>,
  stepTabIndexClicked: number
) {

  const promises = steps
    .filter((step, index) => !step.invisible && index < stepTabIndexClicked)
    /** validates each stepTab to check for errors */
    .map((step) => step.schema?.validate(methods.getValues(), { abortEarly: false })
      .then(
        () => true,
        (error) => {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);

            Object.keys(errors).forEach((propName) => {
              methods.setError(
                propName as any,
                { message: errors[propName] }
              );
            });
          }

          return false;
        }
      ) ?? true);

  const results = await Promise.all(promises);

  const indexNextStepTabWithError = results.findIndex((success) => !success);
  const thereIsAnInvalidForm = indexNextStepTabWithError >= 0;

  const indexNextAvailableStepTab = thereIsAnInvalidForm ? indexNextStepTabWithError : stepTabIndexClicked;

  return {
    stepTabIsAvailable: !thereIsAnInvalidForm,
    indexNextAvailableStepTab,
    toastMessage: GlobalMessages.invalidFormInputs.toString(),
    toastMessageType: "error"
  } as IOnChangeTabResponse;
}

// endregion Functions

const StepTabs: React.FC<IStepTabsProps> = (
  {
    content, children, selectedStepTab, setSelectedStepTab,
    onChangeTab, mode, disableControlButtons, onFinish,
    showConfirmButtonOnLastStepTab, additionalControlButtons
  }
) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [accessedStepTabs, setAccessedStepTabs] = useState<number[]>([0]);
  const [openAdvanceWarningModal, setOpenAdvanceWarningModal] = React.useState(false);
  const [openReturnWarningModal, setOpenReturnWarningModal] = React.useState(false);
  const [stepTabIndexToBeSetWhenClosingWarningModal, setStepTabIndexToBeSetWhenClosingWarningModal] = React.useState(0);
  // endregion States

  // region Advance Warning Modal Handlers
  const handleOpenAdvanceWarningModal = (indexOfStepTabToBeSet: number) => {
    const openModal = content[selectedStepTab].advanceTabWarning?.mode.includes(mode) ?? false;

    setOpenAdvanceWarningModal(openModal);

    if (openModal) {
      setStepTabIndexToBeSetWhenClosingWarningModal(indexOfStepTabToBeSet);

      return;
    }
    setSelectedStepTab(indexOfStepTabToBeSet);
  };

  const handleCloseAdvanceWarningModalOnConfirm = () => {
    setOpenAdvanceWarningModal(false);
    setSelectedStepTab(stepTabIndexToBeSetWhenClosingWarningModal);
  };
  // endregion Advance Warning Modal Handlers

  // region Return Warning Modal Handlers
  const handleOpenReturnWarningModal = (indexOfStepTabToBeSet: number) => {
    const openModal = content[selectedStepTab].returnTabWarning?.mode.includes(mode) ?? false;

    setOpenReturnWarningModal(openModal);

    if (openModal) {
      setStepTabIndexToBeSetWhenClosingWarningModal(indexOfStepTabToBeSet);

      return;
    }
    setSelectedStepTab(indexOfStepTabToBeSet);
  };

  const handleCloseReturnWarningModalOnConfirm = () => {
    setOpenReturnWarningModal(false);
    setSelectedStepTab(stepTabIndexToBeSetWhenClosingWarningModal);
  };
  // endregion Return Warning Modal Handlers

  // region Functions
  const setPreviousStepTab = (stepTabIndex: number) => {
    if (!content[selectedStepTab].returnTabWarning) {
      setSelectedStepTab(stepTabIndex);

      return;
    }

    handleOpenReturnWarningModal(stepTabIndex);
  };

  const setNextStepTab = (stepTabIndex: number) => {
    if (!content[selectedStepTab].advanceTabWarning) {
      setSelectedStepTab(stepTabIndex);

      return;
    }

    handleOpenAdvanceWarningModal(stepTabIndex);
  };

  const changeTab = (indexNewTab: number) => {

    if (indexNewTab <= selectedStepTab) {
      setPreviousStepTab(indexNewTab);

      return;
    }

    if (!onChangeTab) {
      setNextStepTab(indexNewTab);

      return;
    }

    onChangeTab(indexNewTab).then(({
      stepTabIsAvailable, indexNextAvailableStepTab,
      toastMessage, additionalToastMessages, toastMessageType
    }) => {
      if (!stepTabIsAvailable) {
        addToast({
          type: toastMessageType ?? "info",
          title: t(toastMessageType === "info" ? GlobalMessages.toastWarning : GlobalMessages.errorText),
          description: t(toastMessage),
          overwrite: !additionalToastMessages
        });

        if (!_.isEmpty(additionalToastMessages)) {
          additionalToastMessages?.forEach((message) => {
            addToast({
              type: toastMessageType ?? "info",
              title: t(toastMessageType === "info" ? GlobalMessages.toastWarning : GlobalMessages.errorText),
              description: t(message),
              overwrite: true
            });
          });
        }
      }

      setNextStepTab(indexNextAvailableStepTab);
    });
  };
  // endregion Functions

  // region Step tab handlers
  const handleChangeStepTab = (_event: React.SyntheticEvent<Element, Event>, newStepTab: number) => {
    changeTab(newStepTab);
  };

  const nextTab = () => {
    if (lastStepTab) {
      onFinish();

      return;
    }

    for (let i = selectedStepTab + 1; i < content.length; i++) {
      if (!content[i].invisible) {
        changeTab(i);

        return;
      }
    }
  };

  const previousTab = () => {
    if (selectedStepTab <= 0) return;

    const previousAccessedTab = selectedStepTab - 1;

    for (let i = previousAccessedTab; i >= 0; i--) {
      if (!content[i].invisible) {
        setPreviousStepTab(i);

        return;
      }
    }
  };
  // endregion Step tab handlers

  // region Constants
  const getIndexOfLastVisibleTab = () => {
    for (let i = content.length - 1; i >= 0; i--) {
      if (!content[i].invisible) {
        return i;
      }
    }

    return content.length - 1;
  };

  const lastStepTab = (selectedStepTab === getIndexOfLastVisibleTab());
  // endregion Constants

  // region useEffects
  useEffect(() => {
    setAccessedStepTabs((value) => [...new Set([...value, selectedStepTab])]);
  }, [selectedStepTab]);
  // endregion useEffects

  return (
    <>
      <Styled.StepsContainer
        value={selectedStepTab}
        onChange={handleChangeStepTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="scrollable force tabs"
      >
        {content.map(({ id, tabLabel, disabled: disabledProp }, index) => {

          const indexTab = accessedStepTabs.findIndex((value) => value === index);
          const accessedTab = indexTab >= 0;
          const disabled = disabledProp || ((mode === "CREATE") && !accessedTab);

          return (
            <Step
              key={`tab_${id}`}
              label={t(tabLabel)}
              disabled={disabled}
              style={{ display: content[index].invisible ? "none" : "" }}
            />
          );
        })}
      </Styled.StepsContainer>
      {children}
      {!disableControlButtons
        && (
          <Styled.ControlButtons>
            {selectedStepTab > 0 && (
              <Fab
                color="primary"
                size="medium"
                onClick={() => {
                  previousTab();
                }}
              >
                <ArrowBack fontSize="medium" />
              </Fab>
            )}
            {(!lastStepTab || (lastStepTab && showConfirmButtonOnLastStepTab)) && (
              <Fab
                color={lastStepTab ? "success" : "primary"}
                size="large"
                onClick={() => {
                  nextTab();
                }}
              >
                {lastStepTab
                  ? <Check fontSize="large" />
                  : <ArrowForward fontSize="large" />}
              </Fab>
            )}
            {additionalControlButtons?.filter((i) => selectedStepTab >= i.displayFromStepTab).map((button) => (
              <Fab
                key={button.id}
                color={button.color ?? "primary"}
                size={button.size ?? "medium"}
                variant={button.extended ? "extended" : "circular"}
                onClick={button.onClick}
                className="additional-control-button"
              >
                {button.content}
              </Fab>
            ))}
          </Styled.ControlButtons>
        )}
      <ActionDialog
        title={t(content[selectedStepTab].advanceTabWarning?.title ?? GlobalMessages.warningTitleText)}
        open={openAdvanceWarningModal}
        onClose={() => { setOpenAdvanceWarningModal(false); }}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => setOpenAdvanceWarningModal(false) },
          { text: t(GlobalMessages.buttonConfirm), action: handleCloseAdvanceWarningModalOnConfirm }
        ]}
      >
        {t(content[selectedStepTab].advanceTabWarning?.message ?? GlobalMessages.warningMessageText)}
      </ActionDialog>
      <ActionDialog
        title={t(content[selectedStepTab].returnTabWarning?.title ?? GlobalMessages.warningTitleText)}
        open={openReturnWarningModal}
        onClose={() => setOpenReturnWarningModal(false)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => setOpenReturnWarningModal(false) },
          { text: t(GlobalMessages.buttonConfirm), action: handleCloseReturnWarningModalOnConfirm }
        ]}
      >
        {t(content[selectedStepTab].returnTabWarning?.message ?? GlobalMessages.warningMessageText)}
      </ActionDialog>
    </>
  );
};

StepTabs.defaultProps = {
  onChangeTab: undefined,
  disableControlButtons: false,
  showConfirmButtonOnLastStepTab: true,
  additionalControlButtons: undefined
};

export default StepTabs;